import type { EvalCase, EvalRunData, EvalSnapshot, EvalValue } from "@global/types/ai-evals";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { forRun, groupsOf, pct, questionsOf, type Row, type RunKey, rowsOf, score, scopeOf, snapshotOf } from "./scoring";
import { Delta, Pre, StatCard, VerdictChip } from "./ui";

const seconds = (ms: number) => `${Math.round(ms / 1000)}s`;

const RunTable: React.FC<{ data: EvalRunData; rows: Row[]; onOpenRun?: (key: RunKey) => void }> = ({ data, rows, onOpenRun }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Scenario</TableCell>
          <TableCell align="right">Run</TableCell>
          <TableCell align="right">Interview</TableCell>
          <TableCell align="right">Case</TableCell>
          <TableCell align="right">Questions</TableCell>
          <TableCell align="right">Metrics</TableCell>
          <TableCell align="right">Interview time</TableCell>
          <TableCell align="right">Build time</TableCell>
          <TableCell align="right">Credits</TableCell>
          <TableCell>Errors</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...data.snapshots]
          .sort((a, b) => a.scenarioId.localeCompare(b.scenarioId) || a.run - b.run)
          .map((s) => {
            const own = forRun(rows, s);
            return (
              <TableRow key={`${s.scenarioId}-${s.run}`} hover={Boolean(onOpenRun)} sx={{ cursor: onOpenRun ? "pointer" : "default" }} onClick={() => onOpenRun?.(s)}>
                <TableCell>{s.scenarioId}</TableCell>
                <TableCell align="right">{s.run}</TableCell>
                <TableCell align="right">{pct(score(own.filter((r) => scopeOf(r.group) === "interview")))}</TableCell>
                <TableCell align="right">{pct(score(own.filter((r) => scopeOf(r.group) === "case")))}</TableCell>
                <TableCell align="right">{questionsOf(s)}</TableCell>
                <TableCell align="right">{s.case?.metrics.length ?? "–"}</TableCell>
                <TableCell align="right">{seconds(s.durations.interviewMs)}</TableCell>
                <TableCell align="right">{seconds(s.durations.buildMs)}</TableCell>
                <TableCell align="right">{s.creditsUsed ?? "–"}</TableCell>
                <TableCell sx={{ color: s.errors.length ? "error.main" : "text.disabled" }}>{s.errors.join("; ") || "–"}</TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  </TableContainer>
);

export const Runs: React.FC<{ data: EvalRunData; baseline: EvalRunData | null; onOpenRun: (key: RunKey) => void }> = ({ data, baseline, onOpenRun }) => {
  const now = useMemo(() => rowsOf(data), [data]);
  const before = useMemo(() => rowsOf(baseline), [baseline]);
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Click a run for its transcript, summary card, built case and verdicts, side by side with a baseline run of the same scenario.
      </Typography>
      <RunTable data={data} rows={now} onOpenRun={onOpenRun} />
      {baseline && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Baseline
          </Typography>
          <RunTable data={baseline} rows={before} />
        </>
      )}
    </Box>
  );
};

const Transcript: React.FC<{ snapshot: EvalSnapshot }> = ({ snapshot }) => (
  <Stack spacing={1.25}>
    {snapshot.transcript.map((turn, i) =>
      turn.done ? (
        <Typography key={i} variant="body2" color="text.secondary" sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 1.25 }}>
          Interview ended; the summary card follows.
        </Typography>
      ) : (
        <Paper
          key={i}
          variant="outlined"
          sx={(theme) => ({
            p: 1.25,
            maxWidth: "92%",
            alignSelf: turn.role === "user" ? "flex-end" : "flex-start",
            bgcolor: turn.role === "user" ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
            whiteSpace: "pre-wrap",
          })}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {turn.role === "user" ? "Simulated user" : "Agent"}
            {turn.caseTypeId ? ` · type ${turn.caseTypeId}` : ""}
          </Typography>
          <Typography variant="body2">{turn.text}</Typography>
          {turn.steps && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.75 }}>
              {turn.steps.map((step) => (
                <Chip
                  key={step.name}
                  size="small"
                  label={step.name}
                  variant={step.status === "active" ? "filled" : "outlined"}
                  color={step.status === "active" ? "primary" : step.status === "completed" ? "success" : "default"}
                  sx={step.status === "skipped" ? { textDecoration: "line-through" } : undefined}
                />
              ))}
            </Box>
          )}
        </Paper>
      ),
    )}
    {snapshot.errors.length > 0 && <Alert severity="error">{snapshot.errors.join("; ")}</Alert>}
  </Stack>
);

function valueText(v: EvalValue | null): string {
  if (!v) return "–";
  const amount = v.value !== null ? `${v.value}` : `${v.lower} – ${v.upper}`;
  const end = v.end ? ` → ${v.end.value !== null ? v.end.value : `${v.end.lower} – ${v.end.upper}`}` : "";
  return `${amount}${end}`;
}

const Provenance: React.FC<{ v: EvalValue | null }> = ({ v }) =>
  v ? (
    <Typography variant="caption" color="text.secondary" display="block">
      {v.origin}
      {v.source ? ` · ${v.source}` : ""}
    </Typography>
  ) : null;

const CaseView: React.FC<{ model: EvalCase | null }> = ({ model }) => {
  if (!model) return <Typography color="text.secondary">No case was built.</Typography>;
  const facts: [string, React.ReactNode][] = [
    ["Description", model.description ?? "–"],
    ["Baseline", model.baselineDescription ?? "–"],
    ["Go date", model.goDate ?? "–"],
    ["Window", model.window ? `${model.window.start} – ${model.window.end}, ${model.window.grid}` : "–"],
    [
      "Investment",
      model.investment ? (
        <>
          {model.investment.name} · metric {model.investment.metric ?? "–"}
          {model.investment.options.map((o) => (
            <div key={o.name}>
              <b>{o.name}</b> {o.description}
            </div>
          ))}
        </>
      ) : (
        "–"
      ),
    ],
    ["Goal", model.goal ? `${model.goal.name}: ${model.goal.above ? "≥" : "≤"} ${model.goal.value} on ${model.goal.kpi ?? model.goal.metric} by ${model.goal.date ?? "–"}` : "–"],
  ];
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "4px 16px" }}>
        {facts.map(([label, value]) => (
          <React.Fragment key={label}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" component="div">
              {value}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Metrics ({model.metrics.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Per period</TableCell>
              <TableCell>Values</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {model.metrics.map((m) => (
              <TableRow key={m.name}>
                <TableCell>
                  <b>{m.name}</b>
                  {m.name === model.cashflowMetric && <Chip size="small" label="cash flow" sx={{ ml: 1 }} />}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {m.context}
                  </Typography>
                </TableCell>
                <TableCell>{m.type}</TableCell>
                <TableCell>{m.perPeriod ? (m.unit ?? "per period") : "–"}</TableCell>
                <TableCell>
                  {valueText(m.defaultValue)}
                  <Provenance v={m.defaultValue} />
                  {m.overrides.map((o, i) => (
                    <Box key={i} sx={{ mt: 0.5 }}>
                      {o.association} {o.from ?? "…"}–{o.to ?? "…"}: {valueText(o.value)}
                      <Provenance v={o.value} />
                    </Box>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Connectors ({model.connectors.length})
      </Typography>
      <Pre>{model.connectors.map((c) => `${c.from} → ${c.to} (${c.handle})`).join("\n")}</Pre>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Fields
      </Typography>
      {model.fields.length === 0 ? (
        <Typography color="text.secondary">None.</Typography>
      ) : (
        model.fields.map((f) => (
          <Box key={f.name} sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {f.name}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {f.value}
            </Typography>
          </Box>
        ))
      )}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Executive summary
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {model.executiveSummary ?? "–"}
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Model guide
      </Typography>
      {model.overview.length === 0 ? (
        <Typography color="text.secondary">None.</Typography>
      ) : (
        model.overview.map((o, i) => (
          <Typography key={i} variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
            {o}
          </Typography>
        ))
      )}
    </Box>
  );
};

type RunTab = "verdicts" | "transcript" | "summary" | "case" | "scenario";

interface RunDetailProps {
  runKey: RunKey;
  data: EvalRunData;
  baseline: EvalRunData | null;
  onBack: () => void;
  onOpenRun: (key: RunKey) => void;
  onOpenCheck: (id: string) => void;
}

export const RunDetail: React.FC<RunDetailProps> = ({ runKey, data, baseline, onBack, onOpenRun, onOpenCheck }) => {
  const [tab, setTab] = useState<RunTab>("verdicts");
  const baseRuns = baseline?.snapshots.filter((s) => s.scenarioId === runKey.scenarioId).map((s) => s.run).sort() ?? [];
  const [compareRun, setCompareRun] = useState<number | "">(baseRuns[0] ?? "");
  const snapshot = snapshotOf(data, runKey);
  const rows = forRun(rowsOf(data), runKey);
  const baseKey = compareRun === "" ? null : { scenarioId: runKey.scenarioId, run: compareRun };
  const baseSnapshot = baseKey ? snapshotOf(baseline, baseKey) : null;
  const baseRows = baseKey ? forRun(rowsOf(baseline), baseKey) : [];
  const siblings = data.snapshots.filter((s) => s.scenarioId === runKey.scenarioId).map((s) => s.run).sort();
  const scenario = data.scenarios[runKey.scenarioId];
  if (!snapshot) return <Typography>Unknown run.</Typography>;
  const inScope = (list: Row[], scope: "interview" | "case") => score(list.filter((r) => scopeOf(r.group) === scope));

  const columns = (render: (s: EvalSnapshot) => React.ReactNode) =>
    baseSnapshot ? (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            This run · #{runKey.run}
          </Typography>
          {render(snapshot)}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Baseline · #{baseKey?.run} ({baseSnapshot.architecture})
          </Typography>
          {render(baseSnapshot)}
        </Box>
      </Box>
    ) : (
      render(snapshot)
    );

  const baseById = new Map(baseRows.map((r) => [r.id, r]));
  const checkText = new Map(data.checks.map((c) => [c.id, c.text]));

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
        Runs
      </Button>
      <Typography variant="h5">
        {scenario?.title ?? runKey.scenarioId} · run #{runKey.run}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ my: 1 }}>
        {siblings.map((n) => (
          <Chip key={n} label={`#${n}`} color={n === runKey.run ? "primary" : "default"} onClick={() => onOpenRun({ scenarioId: runKey.scenarioId, run: n })} />
        ))}
      </Stack>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", my: 2 }}>
        <StatCard label="Interview" value={pct(inScope(rows, "interview"))} detail={baseSnapshot && <Delta now={inScope(rows, "interview")} before={inScope(baseRows, "interview")} />} />
        <StatCard label="Case" value={pct(inScope(rows, "case"))} detail={baseSnapshot && <Delta now={inScope(rows, "case")} before={inScope(baseRows, "case")} />} />
        <StatCard label="Questions" value={questionsOf(snapshot)} />
        <StatCard label="Metrics" value={snapshot.case?.metrics.length ?? "–"} />
      </Box>
      {snapshot.errors.length > 0 && <Alert severity="error" sx={{ mb: 2 }}>{snapshot.errors.join("; ")}</Alert>}
      {baseRuns.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 220, mb: 1 }}>
          <InputLabel>Compare with baseline run</InputLabel>
          <Select label="Compare with baseline run" value={compareRun} onChange={(e) => setCompareRun(e.target.value === "" ? "" : Number(e.target.value))}>
            <MenuItem value="">none</MenuItem>
            {baseRuns.map((n) => (
              <MenuItem key={n} value={n}>
                #{n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Tabs value={tab} onChange={(_e, value: RunTab) => setTab(value)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tab value="verdicts" label="Verdicts" />
        <Tab value="transcript" label="Transcript" />
        <Tab value="summary" label="Summary card" />
        <Tab value="case" label="Built case" />
        <Tab value="scenario" label="Scenario" />
      </Tabs>

      {tab === "verdicts" &&
        groupsOf(data).map(([group, title]) => {
          const list = rows.filter((r) => r.group === group);
          if (list.length === 0) return null;
          return (
            <Box key={group} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <b>{group}</b> {title} · {pct(score(list))} {baseSnapshot && <Delta now={score(list)} before={score(baseRows.filter((r) => r.group === group))} />}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Check</TableCell>
                      <TableCell>Verdict</TableCell>
                      <TableCell>Evidence</TableCell>
                      {baseSnapshot && <TableCell>Baseline</TableCell>}
                      {baseSnapshot && <TableCell>Baseline evidence</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((r) => {
                      const b = baseById.get(r.id);
                      return (
                        <TableRow key={r.id}>
                          <TableCell sx={{ cursor: "pointer" }} onClick={() => onOpenCheck(r.id)}>
                            <b>{r.id}</b> {checkText.get(r.id)}
                          </TableCell>
                          <TableCell>
                            <VerdictChip verdict={r.verdict} />
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{r.evidence}</TableCell>
                          {baseSnapshot && <TableCell>{b ? <VerdictChip verdict={b.verdict} /> : "–"}</TableCell>}
                          {baseSnapshot && <TableCell sx={{ color: "text.secondary" }}>{b?.evidence}</TableCell>}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      {tab === "transcript" && columns((s) => <Transcript snapshot={s} />)}
      {tab === "summary" && columns((s) => <Pre>{s.summary ?? "No summary."}</Pre>)}
      {tab === "case" && columns((s) => <CaseView model={s.case} />)}
      {tab === "scenario" && <Pre>{JSON.stringify(scenario, null, 2)}</Pre>}
    </Box>
  );
};
